<script lang="ts" setup>
import type { GetTeamsQuery, GetTeamsQueryVariables } from './TeamModal.generated'
import { CreateTeamMutationDocument, GetTeamsDocument, UpdateTeamMutationDocument } from './TeamModal.generated'

export type Mode = 'create' | 'edit'

const props = defineProps<{ mode: Mode, id?: string }>()
const open = defineModel<boolean>({ required: true })

interface Team { id: string, name: string }

const form = useTemplateRef<HTMLFormElement>('form')
const state = reactive<{
  name: string
  members: string[]
  parentTeam: Team
}>({ name: '', members: [], parentTeam: { id: '', name: '' } })

const { fetchTeam, isFetching: isLoadingTeamDetails } = useTeam()

const { isFetching: isFetchingTeams, search: searchTeams } = useRelayField<
  GetTeamsQuery,
  GetTeamsQueryVariables,
  Team[]
>(
  GetTeamsDocument,
  { first: 50, term: '' },
  ({ data }) =>
    getListOfNodes(data?.organization.teams)
      .filter(node => node.id !== props.id),
  ['teams'],
)

const { execute: createTeam, isFetching: isCreatingTeam } = useMutation(
  CreateTeamMutationDocument,
  { refetchTags: [`teams`] },
)

const { execute: updateTeam, isFetching: isUpdatingTeam } = useMutation(
  UpdateTeamMutationDocument,
  { refetchTags: [`teams`] },
)

watch(open, async (newOpen) => {
  if (!newOpen)
    return

  state.name = ''
  state.members = []
  state.parentTeam = { id: '', name: '' }

  if (props.mode === 'edit' && props.id) {
    const { name, members, parent } = await fetchTeam(props.id)
    state.name = name
    state.members = members
    state.parentTeam = parent
  }
})

async function save() {
  let response

  if (props.mode === 'create') {
    response = await createTeam({
      input: {
        name: state.name,
        members: state.members,
        parent: state.parentTeam.id,
      },
    })
  }
  else { // update
    response = await updateTeam({
      input: {
        id: props.id ?? '',
        name: state.name,
        members: state.members,
        parent: state.parentTeam.id,
      },
    })
  }

  const error = handleMutationError({ errors: response.data?.mutateTeam?.errors, combinedError: response.error })
  return !error
}

async function onSubmit() {
  const name: HTMLInputElement = form.value?.$el.querySelector('[name="name"]')
  if (name && !name.checkValidity()) {
    name.reportValidity()
    return
  }
  const success = await save()

  if (success)
    open.value = false
}

function clearParent() {
  state.parentTeam = { id: '', name: '' }
}
</script>

<template>
  <UModal v-model="open" :ui="{ width: 'w-full sm:max-w-xl' }">
    <UCard class="bg-white">
      <UButton
        icon="light:xmark"
        variant="ghost"
        color="gray"
        class="absolute right-2 top-2"
        @click="open = false"
      />

      <div class="text-lg font-semibold">
        {{ mode === 'create' ? 'Create a new team' : 'Edit team' }}
      </div>
      <div class="mb-4 text-base text-gray-600 dark:text-gray-400">
        Team membership is used for data scoping
      </div>

      <UForm ref="form" :state="state" @submit="onSubmit">
        <FormGroup
          label="Name"
          required
          class="mb-4"
          size="md"
          name="name"
          help="You can use your department or team name. Keep it simple."
          :disabled="isLoadingTeamDetails || isCreatingTeam || isUpdatingTeam"
        >
          <UInput v-model="state.name" autofocus required />
        </FormGroup>
        <FormGroup
          label="Parent team"
          size="md"
          name="parent"
          help=""
          class="my-4"
          :disabled="isLoadingTeamDetails || isCreatingTeam || isUpdatingTeam"
        >
          <SelectMenu
            v-model="state.parentTeam.id"
            :is-fetching="isFetchingTeams"
            :searchable="searchTeams"
            :clearable="Boolean(state.parentTeam.id)"
            placeholder="Not selected"
            option-attribute="name"
            value-attribute="id"
            selected-icon="light:check"
            size="md"
            @clear="clearParent"
          />
        </FormGroup>
        <FormGroup
          label="Members"
          size="md"
          name="members"
          help="You can edit your team members at any time."
          :disabled="isLoadingTeamDetails || isCreatingTeam || isUpdatingTeam"
        >
          <UsersSelector v-model="state.members" show-contributors />
        </FormGroup>
      </UForm>

      <template #footer>
        <div class="flex items-center justify-end gap-2">
          <UButton
            size="sm"
            variant="ghost"
            color="gray"
            @click="open = false"
          >
            Cancel
          </UButton>
          <UButton
            size="sm"
            :loading="isCreatingTeam || isUpdatingTeam"
            :disabled="isLoadingTeamDetails || isCreatingTeam || isUpdatingTeam"
            @click="form?.submit()"
          >
            Save
          </UButton>
        </div>
      </template>
    </UCard>
  </UModal>
</template>
